import { useMemo, useState } from 'react';
import { Mail } from 'lucide-react';
import { toast } from 'react-toastify';

import { Modal } from '../../../../components/Modal/Modal';
import { useBulkSendOrderList } from '../../api';
import { customerIdOf, distinctCustomers } from '../grain';
import { summarizeBulk } from '../summarize';
import type { BulkAction, BulkActionBarContext } from '../types';

// Email each distinct customer in the selection their current open-order list.
// This is per-customer, so the specific loads only pick WHICH customers to email.
function SendOrderListDialog({ ctx, onClose }: { ctx: BulkActionBarContext; onClose: () => void }) {
  const { rows, count } = ctx;
  const bulkSend = useBulkSendOrderList();
  const isApplying = bulkSend.isPending;

  const customers = useMemo(() => distinctCustomers(rows), [rows]);
  const rowsWithoutCustomer = count - rows.filter((row) => customerIdOf(row)).length;

  const apply = async () => {
    if (customers.length === 0) return;

    try {
      const res = await bulkSend.mutateAsync({ customers });
      const summary = summarizeBulk(
        res.succeeded,
        res.failed,
        customers.length,
        'emailed',
        'customer'
      );
      if (summary.tone === 'success') toast.success(summary.message);
      else toast.warn(summary.message);
      if (summary.clearSelection) ctx.onClearSelection();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Failed to send the order lists.');
    }
  };

  return (
    <Modal
      isOpen
      onClose={isApplying ? () => {} : onClose}
      title={`Send the order list to ${customers.length} customer${
        customers.length === 1 ? '' : 's'
      }`}
      size="sm"
      footer={
        <>
          <button className="btn btn--ghost" onClick={onClose} disabled={isApplying}>
            Cancel
          </button>
          <button
            className="btn btn--primary"
            onClick={apply}
            disabled={isApplying || customers.length === 0}
          >
            {isApplying ? 'Sending…' : `Send to ${customers.length}`}
          </button>
        </>
      }
    >
      <p>
        The {count} selected load{count === 1 ? '' : 's'} span <strong>{customers.length}</strong>{' '}
        customer{customers.length === 1 ? '' : 's'}. Each one gets an email with{' '}
        <strong>their whole current open-order list</strong> — not just the selected loads.
      </p>

      {rowsWithoutCustomer > 0 && (
        <div className="bulk-confirm__warning">
          {rowsWithoutCustomer} selected load{rowsWithoutCustomer === 1 ? '' : 's'} have no customer
          and are ignored.
        </div>
      )}
    </Modal>
  );
}

function SendOrderListTrigger({ ctx }: { ctx: BulkActionBarContext }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button type="button" className="btn btn--secondary btn--sm" onClick={() => setOpen(true)}>
        <Mail size={14} />
        Send order list
      </button>

      {open && <SendOrderListDialog ctx={ctx} onClose={() => setOpen(false)} />}
    </>
  );
}

export const SendOrderListAction: BulkAction = {
  id: 'send-order-list',
  isAvailable: (ctx) => ctx.rows.some((row) => customerIdOf(row)),
  Component: SendOrderListTrigger,
};
